import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { newGroupId } from '../editor/arrange';
import { cleanFx, fxCss, SET_GAP, STYLE_CATS, styleFonts, TEXT_STYLES, type StyleBox, type StyleCat, type TextFx, type TextStyle } from '../editor/textFx';
import { setTextFx, textInk } from '../editor/textFxDraw';
import { api, type SavedTextStyle } from '../sync/api';
import { shortText } from '../print/rules';
import { fontNeeds, loadFonts } from '../fonts/load';
import { isThaiText, type ThaiTextbox } from '../thai/ThaiTextbox';
import { $ } from './dom';
import { makeText } from './objects';
import { closeSheets } from './sheets';
import { toast } from './toast';

/*
 * ข้อความ → "แบบข้อความสำเร็จ" (choices 25 ต.ค. 2569): tap a style —
 *   no text chosen   new text box(es) on the page (a set of several boxes is added together, as one group)
 *   a text chosen    it takes the style (font, colour, spacing, effect); its words, size and width stay
 * แบบของกลุ่ม (0021, ข): admins and members with the right 'text_styles' save the chosen text box(es) as a style for
 * everyone; whoever saved one (or an admin) deletes it. To change one: use it, fix it, save it again.
 * The previews are HTML drawn with the same numbers as the canvas (src/editor/textFx.ts fxCss).
 */
export interface TextStyleHooks {
  /** a Thai reason when the page cannot be changed now (someone else's work, a page still loading), else null */
  cannotEdit: () => string | null;
  /** may save styles for the group (admin, or the right 'text_styles'; the server checks too) */
  canSave: () => boolean;
  isAdmin: () => boolean;
}

const PREVIEW_PX = 0.2;   // preview font size: a part of the style's own size, kept between 12 and 30 px

/** the words a style's preview shows (its own) */
function preview(st: TextStyle): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'tspre';
  for (const b of st.boxes) {
    const line = document.createElement('span');
    // long words are drawn smaller so the whole preview fits its tile (about 140 px wide)
    const px = Math.max(10, Math.min(30, b.size * PREVIEW_PX, 260 / Math.max(1, [...b.text].length)));
    line.textContent = b.text;
    Object.assign(line.style, {
      fontFamily: `'${b.font}', 'Sarabun', sans-serif`, fontWeight: String(b.weight), fontSize: `${px}px`,
      letterSpacing: b.spacing ? `${b.spacing / 1000}em` : '0',
      ...fxCss(b.fx ? { ...b.fx, ink: b.ink } as TextFx : null, px, b.ink)
    });
    wrap.append(line);
  }
  return wrap;
}

export function initTextStyles(ed: Editor, history: History, h: TextStyleHooks) {
  const { canvas } = ed;
  const grid = $('tsGrid');
  let fontsAsked = false;

  let saved: SavedTextStyle[] = [];
  let armed: string | null = null;   // a saved style whose ✕ was tapped once (the next tap deletes it)

  /** one tile (in its holder, so a saved style can carry its ✕ next to it) */
  function tile(st: TextStyle): HTMLElement {
    const item = document.createElement('div');
    item.className = 'tsitem';
    item.dataset.cat = st.cat;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tstile' + (st.boxes.length > 1 ? ' tsset' : '');
    b.setAttribute('aria-label', `${st.th}${st.boxes.length > 1 ? ` (ชุด ${st.boxes.length} กล่อง)` : ''}${st.saved ? ` · ของกลุ่ม โดย ${st.saved.by}` : ''}`);
    b.append(preview(st));
    b.onclick = () => void use(st);
    item.append(b);
    if (st.saved) {
      item.dataset.saved = '1';
      const by = document.createElement('small'); by.className = 'tsby'; by.textContent = st.th;
      item.append(by);
      if (h.isAdmin() || (st.saved.mine && h.canSave())) {
        const x = document.createElement('button');
        x.type = 'button'; x.className = 'tsdel'; x.textContent = armed === st.id ? 'ลบ?' : '✕';
        x.setAttribute('aria-label', `ลบแบบ "${st.th}"`);
        x.onclick = () => void remove(st, x);
        item.append(x);
      }
    }
    return item;
  }
  for (const st of TEXT_STYLES) grid.append(tile(st));

  /*
   * หมวด (ก, 25 ต.ค. 2569): the tiles are made once; a chip only shows/hides them (nothing is made again under the finger)
   */
  const cats = $('tsCats');
  let cat = '';
  const CHIPS = [{ id: '', th: 'ทั้งหมด' }, { id: 'group', th: 'ของกลุ่ม' }, ...STYLE_CATS];
  const shows = (item: HTMLElement) => !cat || (cat === 'group' ? item.dataset.saved === '1' : item.dataset.cat === cat);
  function paintCats() {
    cats.querySelectorAll<HTMLButtonElement>('button').forEach(b => {
      b.setAttribute('aria-pressed', String(b.dataset.cat === cat));
      const n = [...grid.querySelectorAll<HTMLElement>('.tsitem')].filter(i => !b.dataset.cat || (b.dataset.cat === 'group' ? i.dataset.saved === '1' : i.dataset.cat === b.dataset.cat)).length;
      b.textContent = `${CHIPS.find(c => c.id === b.dataset.cat)!.th} ${n}`;
    });
    grid.querySelectorAll<HTMLElement>('.tsitem').forEach(t => { t.hidden = !shows(t); });
    if (cat === 'group' && !saved.length) $('tsNote').textContent = h.canSave() ? 'ยังไม่มีแบบของกลุ่ม เลือกข้อความบนหน้าแล้วกด "บันทึกเป็นแบบของกลุ่ม" ด้านล่าง' : 'ยังไม่มีแบบของกลุ่ม ผู้ดูแลหรือคนที่ได้สิทธิ์เป็นคนบันทึก';
    else if ($('tsNote').dataset.fonts !== '1') $('tsNote').textContent = '';
  }
  for (const c of CHIPS) {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'fchip'; b.dataset.cat = c.id;
    b.onclick = () => { cat = c.id; paintCats(); };
    cats.append(b);
  }
  paintCats();

  /* ---------- แบบของกลุ่ม (0021) ---------- */
  const asStyle = (s: SavedTextStyle): TextStyle => ({ id: s.id, cat: s.cat, th: s.name, boxes: s.boxes, saved: { by: s.createdByName || '—', mine: s.mine } });
  /** the saved styles' tiles (only they are made again; the built-in ones stay) */
  function paintSaved() {
    grid.querySelectorAll('.tsitem[data-saved]').forEach(e => e.remove());
    const first = grid.firstChild;
    for (const s of saved) grid.insertBefore(tile(asStyle(s)), first);
    paintCats();
    $('tsSaveOpen').hidden = !h.canSave();
  }
  async function loadSaved() {
    try { saved = (await api.textStyles()).styles; }
    catch (e) { saved = []; $('tsNote').textContent = `โหลดแบบของกลุ่มไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`; }
    paintSaved();
    // their fonts, for the previews
    void loadFonts(fontNeeds(saved.flatMap(s => s.boxes).map(b => ({ type: 'thaiTextbox', fontFamily: b.font, fontWeight: b.weight }))));
  }
  async function remove(st: TextStyle, x: HTMLButtonElement) {
    if (armed !== st.id) {
      armed = st.id; x.textContent = 'ลบ?';
      setTimeout(() => { if (armed === st.id) { armed = null; x.textContent = '✕'; } }, 4000);
      return;
    }
    armed = null;
    try { await api.deleteTextStyle(st.id); toast(`ลบแบบ "${st.th}" แล้ว งานที่ใช้ไปแล้วไม่เปลี่ยน`); }
    catch (e) { toast(`ลบไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`, true); }
    await loadSaved();
  }

  /** the chosen text box(es) as a style: look, words, and where each sits from the set's centre (on a 1080 px page) */
  function capture(): { boxes: StyleBox[]; turned: boolean } | string {
    const a = canvas.getActiveObject();
    if (!a) return 'เลือกข้อความบนหน้าก่อน (กล่องเดียว หรือหลายกล่องพร้อมกัน)';
    if ((a as fabric.IText).isEditing) return 'กดเสร็จจากการพิมพ์ก่อน แล้วค่อยบันทึก';
    const sel = a.type === 'activeSelection' ? a as fabric.ActiveSelection : null;
    const list = sel ? sel.getObjects() : [a];
    const texts = list.filter(isThaiText);
    if (!texts.length) return 'บันทึกได้เฉพาะข้อความ — ที่เลือกไว้ไม่มีข้อความ';
    if (texts.length !== list.length) return `บันทึกได้เฉพาะข้อความ — ที่เลือกไว้มีชิ้นอื่นปนอยู่ ${list.length - texts.length} ชิ้น เลือกใหม่ให้เหลือแต่ข้อความ`;
    if (texts.length > 12) return 'บันทึกได้ไม่เกิน 12 กล่องต่อแบบ';
    const u = ed.unit();
    const m = sel ? sel.calcTransformMatrix() : null;
    const ks = sel ? Math.abs(sel.scaleY ?? 1) : 1, kx = sel ? Math.abs(sel.scaleX ?? 1) : 1;
    const centres = texts.map(t => m ? fabric.util.transformPoint(t.getCenterPoint(), m) : t.getCenterPoint());
    const xs = centres.map(c => c.x), ys = centres.map(c => c.y);
    const mid = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
    let turned = !!(sel?.angle);
    const boxes = texts.map((t, k): StyleBox => {
      if (t.angle) turned = true;
      const fx = cleanFx((t as { textFx?: unknown }).textFx);
      const w = Number(t.fontWeight) || (t.fontWeight === 'bold' ? 700 : 400);
      const r = (n: number) => Math.round(n * 100) / 100;
      return {
        text: String(t.text ?? ''), font: String(t.fontFamily ?? 'Sarabun').replace(/^["']|["']$/g, ''), weight: w,
        size: r((t.fontSize ?? 40) * Math.abs(t.scaleY ?? 1) * ks / u), ink: textInk(t).toLowerCase(),
        spacing: t.charSpacing ?? 0, lineHeight: t.lineHeight ?? 1.35, align: (t.textAlign ?? 'left') as StyleBox['align'],
        width: r((t.width ?? 100) * Math.abs(t.scaleX ?? 1) * kx / u), dx: r((centres[k].x - mid.x) / u), dy: r((centres[k].y - mid.y) / u),
        fx: fx ? (({ ink: _i, ...rest }) => rest)(fx) : null
      };
    });
    return { boxes, turned };
  }

  const saveBox = $('tsSave'), saveMsg = $('tsSaveMsg');
  const tell = (t: string, ok = false) => { saveMsg.textContent = t; saveMsg.hidden = !t; saveMsg.classList.toggle('ok', ok); };
  $('tsSaveOpen').onclick = () => {
    saveBox.hidden = !saveBox.hidden;
    if (saveBox.hidden) return;
    tell('');
    const sel = $<HTMLSelectElement>('tsSaveCat');
    if (!sel.options.length) for (const c of STYLE_CATS) sel.append(new Option(c.th, c.id));
    const got = capture();
    if (typeof got === 'string') { tell(got); return; }
    if (!$<HTMLInputElement>('tsSaveName').value.trim()) $<HTMLInputElement>('tsSaveName').value = shortText(got.boxes[0].text.replace(/\s+/g, ' '), 40);
  };
  $('tsSaveGo').onclick = async () => {
    const got = capture();
    if (typeof got === 'string') { tell(got); return; }
    const btn = $<HTMLButtonElement>('tsSaveGo');
    btn.disabled = true; tell('กำลังบันทึก…', true);
    try {
      const r = await api.saveTextStyle({ name: $<HTMLInputElement>('tsSaveName').value, cat: $<HTMLSelectElement>('tsSaveCat').value as StyleCat, boxes: got.boxes });
      tell(`บันทึก "${r.style.name}" เป็นแบบของกลุ่มแล้ว (${got.boxes.length} กล่อง)${got.turned ? ' · การหมุนไม่ถูกบันทึก แบบนี้จะวางตรงเสมอ' : ''}`, true);
      $<HTMLInputElement>('tsSaveName').value = '';
      cat = 'group';
      await loadSaved();
    } catch (e) { tell(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`); }
    finally { btn.disabled = false; }
  };

  /** the fonts of every style, for the previews (once, when the panel first opens) */
  function onOpen() {
    void loadSaved();   // the group's styles may have changed on another device
    if (fontsAsked) return;
    fontsAsked = true;
    const all = TEXT_STYLES.flatMap(styleFonts).map(f => ({ type: 'thaiTextbox', fontFamily: f.family, fontWeight: f.weight }));
    void loadFonts(fontNeeds(all)).then(failed => {
      if (failed.length) $('tsNote').dataset.fonts = '1';
      if (failed.length) $('tsNote').textContent = `โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตัวอย่างบางแบบแสดงด้วยฟอนต์สำรอง (ตรวจอินเทอร์เน็ต)`;
    });
  }

  /** a box's look on a text (its words, size and width are not touched) */
  function styleText(t: ThaiTextbox, b: StyleBox) {
    t.set({ fontFamily: b.font, fontWeight: b.weight, charSpacing: b.spacing ?? 0, ...(b.lineHeight ? { lineHeight: b.lineHeight } : {}) } as Partial<ThaiTextbox>);
    setTextFx(t, b.fx ? { ...b.fx, ink: b.ink } as TextFx : null);
    if (!b.fx) t.set('fill', b.ink);
    t.initDimensions();
    t.setCoords();
  }

  /** a new box for a style box: as wide as its words (a พื้นหลัง box hugs them), centred */
  function newBox(b: StyleBox): ThaiTextbox {
    const u = ed.unit();
    const t = makeText(b.text, { fontSize: b.size * u, width: b.width ? b.width * u : ed.doc.w, textAlign: b.align ?? 'center' }) as ThaiTextbox;
    styleText(t, b);
    if (b.width) return t;   // a saved style keeps its own width (its words wrap as they did)
    const lines = (t as unknown as { _textLines: string[][] })._textLines;
    const longest = Math.max(...lines.map((_, i) => t.getLineWidth(i)));
    t.set('width', Math.min(ed.doc.w, Math.ceil(longest) + 2));
    t.initDimensions();
    t.setCoords();
    return t;
  }

  async function use(st: TextStyle) {
    const no = h.cannotEdit();
    if (no) { toast(no, true); return; }
    const failed = await loadFonts(fontNeeds(styleFonts(st).map(f => ({ type: 'thaiTextbox', fontFamily: f.family, fontWeight: f.weight }))));
    for (const f of styleFonts(st)) fabric.util.clearFabricFontCache(f.family);
    if (failed.length) toast(`โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตอนนี้แสดงด้วยฟอนต์สำรอง ตรวจอินเทอร์เน็ตแล้วเลือกแบบนี้อีกครั้ง`, true);
    // the page may have changed while the fonts came (another page opened, a work opened read-only)
    const again = h.cannotEdit();
    if (again) { toast(again, true); return; }

    const chosen = canvas.getActiveObject();
    if (chosen && isThaiText(chosen) && !(chosen as fabric.IText).isEditing && !(chosen as { locked?: boolean }).locked) {
      styleText(chosen, st.boxes[0]);
      canvas.requestRenderAll();
      canvas.fire('object:modified', { target: chosen });   // one undo step, toolbar and layers follow (main.ts)
      toast(st.boxes.length > 1
        ? `ใช้แบบ "${st.th}" (กล่องแรกของชุด) กับข้อความที่เลือกแล้ว — ถ้าต้องการทั้งชุด แตะที่ว่างบนหน้าให้ไม่เลือกอะไรก่อน`
        : `ใช้แบบ "${st.th}" กับข้อความที่เลือกแล้ว (คำและขนาดเดิม)`);
      return;
    }

    const boxes = st.boxes.map(newBox);
    const u = ed.unit();
    const gid0 = boxes.length > 1 ? newGroupId() : null;
    if (st.boxes.every(b => b.dx !== undefined && b.dy !== undefined)) {
      // a saved style: each box where it was, from the set's centre — the set in the middle of the page
      boxes.forEach((t, k) => {
        t.setPositionByOrigin(new fabric.Point(ed.doc.w / 2 + st.boxes[k].dx! * u, ed.doc.h / 2 + st.boxes[k].dy! * u), 'center', 'center');
        t.setCoords();
        if (gid0) (t as ThaiTextbox & { groupId?: string }).groupId = gid0;
      });
      return place(boxes);
    }
    const heightOf = (t: ThaiTextbox) => t.getBoundingRect(true, true).height;
    const total = boxes.reduce((s, t, k) => s + heightOf(t) + (k ? SET_GAP * st.boxes[k].size * u : 0), 0);
    let top = (ed.doc.h - total) / 2;
    boxes.forEach((t, k) => {
      if (k) top += SET_GAP * st.boxes[k].size * u;
      // the frame (with a พื้นหลัง box) is what is centred
      const r = t.getBoundingRect(true, true);
      t.set({ left: (t.left ?? 0) + (ed.doc.w - r.width) / 2 - r.left, top: (t.top ?? 0) + top - r.top });
      t.setCoords();
      top += r.height;
      if (gid0) (t as ThaiTextbox & { groupId?: string }).groupId = gid0;
    });
    place(boxes);
  }

  /** onto the page, chosen; one undo step for the whole set (BUG-51): added quietly, then recorded once */
  function place(boxes: ThaiTextbox[]) {
    history.silently(() => boxes.forEach(t => canvas.add(t)));
    canvas.setActiveObject(boxes.length > 1 ? new fabric.ActiveSelection(boxes, { canvas }) : boxes[0]);
    canvas.requestRenderAll();
    canvas.fire('object:modified', { target: boxes[0] });
    if (!matchMedia('(min-width: 900px)').matches) closeSheets();
  }

  return { onOpen };
}
